import { MoreVertical, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { CopyButton } from '@/components/copy-button';
import { ProtectedComponent } from '@/components/ProtectedComponent';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import {
  useDeleteKnowledgeBaseMutation,
  useSyncKnowledgeBaseMutation,
} from '@/query/knowledge.query';

import { Permissions } from '@/constants';

import { DATE_FORMAT, formatDate } from '@/lib/date';

import { AddDocumentDialog } from './add-document-dialog';

import useAlertStore from '@/store/alert.store';
import { useKnowledgeModalsStore } from '@/store/knowledge-modals.store';
import useModalStore from '@/store/modal.store';

export function KnowledgeBaseHeader() {
  const knowledgeBase = useKnowledgeModalsStore(state => state.selectedKnowledgeBase);
  const openModal = useModalStore(state => state.openModal);
  const { openAlert } = useAlertStore();
  const syncMutation = useSyncKnowledgeBaseMutation();
  const deleteMutation = useDeleteKnowledgeBaseMutation();

  if (!knowledgeBase) return null;

  const handleSync = () => {
    syncMutation.mutate(knowledgeBase.kb_id);
  };

  const handleDelete = () => {
    openAlert({
      title: 'Delete knowledge base',
      description: `Are you sure you want to delete "${knowledgeBase.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      onConfirm: () => deleteMutation.mutate(knowledgeBase.kb_id),
      type: 'destructive',
    });
  };

  return (
    <div className="bg-muted/50 px-3 py-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="font-semibold text-base">{knowledgeBase.name}</h2>
            <CopyButton
              value={knowledgeBase.name}
              tooltip="Copy KB ID"
              className="h-5 w-5"
              iconClassName="h-3.5 w-3.5 opacity-60"
              onCopy={() =>
                toast.success('KB ID copied', {
                  description: 'Knowledge base ID copied to clipboard',
                })
              }
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{knowledgeBase.kb_description}</span>
            {knowledgeBase.updated_at && (
              <>
                <span className="text-muted-foreground/40">·</span>
                <span>Last Updated {formatDate(knowledgeBase.updated_at, DATE_FORMAT.FULL)}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ProtectedComponent requiredPermissions={[Permissions.KnowledgeBaseCreateUpdate]}>
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => openModal(() => <AddDocumentDialog />)}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Document
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={handleSync}
              disabled={syncMutation.isPending}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 mr-1.5 ${syncMutation.isPending ? 'animate-spin' : ''}`}
              />
              Sync
            </Button>
          </ProtectedComponent>

          <ProtectedComponent requiredPermissions={[Permissions.KnowledgeBaseDelete]}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <MoreVertical className="h-4 w-4 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleDelete} variant="destructive">
                  <Trash2 className="h-4 w-4" />
                  Delete knowledge base
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </ProtectedComponent>
        </div>
      </div>
    </div>
  );
}
